import { toneOfRatio, type Tone } from "./tone";
import type { RetailCriterion, RetailRow } from "./types";
import { fmt, fmt1, fmtPct1 } from "./format";

/**
 * Представление данных розницы без React (этап 3, решения владельца 28.10.2026):
 * - всё в процентах, как в CRM-кабинете; «баллы» только у компетенций;
 * - KPI критерия не ограничен сверху; процент выполнения — факт к плану, не из KPI;
 * - компетенции: крупно оценка в баллах (fact из панели), KPI = база (maxPoints) + личная надбавка;
 * - формул нет, нулевые строки выплаты не показываются.
 */

/** Критерии, у которых plan/fact — деньги в грн. */
const MONEY_KEYS = new Set(["plan", "shifts", "shiftsInfo", "avg", "avgOwn", "avgStore"]);

export interface CriterionView {
  key: string;
  /** подпись панели без «(справочно)» — приложение локализует по key, это запасной вариант */
  label: string;
  /** крупное значение: факт («785 292 грн»), счётчик («0»), «70 баллов» или KPI */
  fact: string;
  /** подпись под значением: «план … грн», «базовое начисление» или "" */
  plan: string;
  /** значение справа: «22,7 %» или «15,0 % + 15,0 %»; у справочной строки null */
  kpiText: string | null;
  /** подпись под KPI: «227,3 % плана», «из 10 %», «основные + дополнительные» или "" */
  sub: string;
  /** заполнение полосы 0..1 (уже обрезано) */
  ratio: number;
  /** цвет полосы по выполнению */
  tone: Tone;
  info: boolean;
  competence: boolean;
  /** у компетенций: база и надбавка в % (надбавка 0, если её нет) */
  competenceBase?: number;
  competenceExtra?: number;
}

export type CriterionLabels = {
  labelOf: (key: string, fallback: string) => string;
  ofPct: string;      // «из»
  planWord: string;   // «план» (подпись под фактом)
  ofPlan: string;     // «плана» («… % плана»)
  points: string;     // «баллов»
  baseAccrual: string;
  baseExtra: string;
};

export function criterionView(c: RetailCriterion, L: CriterionLabels): CriterionView {
  const money = MONEY_KEYS.has(c.key);
  const unit = money ? " грн" : "";
  const label = L.labelOf(c.key, c.label.replace(/\s*\(справочно\)\s*$/i, ""));
  const hasPlan = c.plan != null && c.fact != null && c.plan > 0;

  let fact = "";
  let plan = "";
  if (c.key === "competence") {
    fact = c.fact != null ? `${fmt(c.fact)} ${L.points}` : `— ${L.points}`;
    plan = L.baseAccrual;
  } else if (c.plan != null && c.fact != null) {
    fact = fmt(c.fact) + unit;
    plan = `${L.planWord} ${fmt(c.plan)}${unit}`;
  } else if (c.count != null) {
    fact = c.count2 != null ? `${c.count} / ${c.count2}` : String(c.count);
  } else {
    fact = fmtPct1(c.points);
  }

  if (c.info) {
    return { key: c.key, label, fact, plan, kpiText: null, sub: "", ratio: 0, tone: "norm", info: true, competence: false };
  }

  if (c.key === "competence") {
    const base = c.maxPoints;
    const extra = Math.max(0, round2(c.points - c.maxPoints));
    const kpiText = extra > 0 ? `${fmtPct1(base)} + ${fmtPct1(extra)}` : fmtPct1(c.points);
    const sub = extra > 0 ? L.baseExtra : "";
    const ratio = c.maxPoints > 0 ? c.points / c.maxPoints : 1;
    return {
      key: c.key, label, fact, plan, kpiText, sub, ratio: clamp01(ratio), tone: toneOf(ratio),
      info: false, competence: true, competenceBase: base, competenceExtra: extra,
    };
  }

  const ratio = hasPlan ? (c.fact as number) / (c.plan as number) : (c.maxPoints > 0 ? c.points / c.maxPoints : 1);
  const sub = hasPlan ? `${fmt1(ratio * 100)} % ${L.ofPlan}` : `${L.ofPct} ${fmtPct1(c.maxPoints)}`;
  return { key: c.key, label, fact, plan, kpiText: fmtPct1(c.points), sub, ratio: clamp01(ratio), tone: toneOf(ratio), info: false, competence: false };
}

export interface PayoutRow { key: "gross" | "advance" | "deductions" | "extras" | "sick" | "vacation"; sign: "" | "−" | "+"; value: number }

/** Строки карточки «К выплате» без формул; нулевые пропускаются. net отдельно. */
export function payoutRows(r: RetailRow): PayoutRow[] {
  const rows: PayoutRow[] = [
    { key: "gross", sign: "", value: r.gross },
    { key: "advance", sign: "−", value: r.advance },
    { key: "deductions", sign: "−", value: r.deductions },
    { key: "extras", sign: "+", value: r.extras },
    { key: "sick", sign: "+", value: r.sick_pay },
    { key: "vacation", sign: "+", value: r.vacation_pay },
  ];
  return rows.filter(x => Math.abs(x.value) >= 0.005);
}

export interface KpiSummary { kpi: number; pct: number; floorNote: number | null }

/** Сводка: итоговый KPI в %, % от чистых продаж по шкале; floorNote — если к расчёту принят минимум. */
export function kpiSummary(r: RetailRow): KpiSummary {
  return { kpi: r.kpi, pct: r.pct, floorNote: r.kpi_used > r.kpi + 0.005 ? r.kpi_used : null };
}

export type ViewId = "all" | `store:${string}` | `p:${string}`;

export function parseView(v: string): { kind: "all" } | { kind: "store"; store: string } | { kind: "person"; personId: string } {
  if (v.startsWith("store:")) return { kind: "store", store: v.slice(6) };
  if (v.startsWith("p:")) return { kind: "person", personId: v.slice(2) };
  return { kind: "all" };
}

// Цвет полосы: ниже 100 % красный, ровно 100 % жёлтый, выше зелёный (решение владельца 28.10.2026, как в CRM KPI).
const toneOf = toneOfRatio;
function clamp01(x: number): number { return Math.max(0, Math.min(1, x)); }
function round2(x: number): number { return Math.round(x * 100) / 100; }

/** Справочная строка «все чеки» (включая до 1000 грн); null, если панель поля не прислала. */
export function checksAllNote(parts: Array<{ checks_all_count: number | null; checks_all_sum: number | null; checks_all_avg: number | null }>): { count: number; avg: number | null } | null {
  const present = parts.filter(p => p.checks_all_count != null);
  if (present.length === 0) return null;
  const count = present.reduce((a, p) => a + (p.checks_all_count ?? 0), 0);
  if (present.length === 1) return { count, avg: present[0]!.checks_all_avg ?? null };
  // Несколько магазинов: средний суммарно (Σsum / Σcount), не среднее от средних.
  const sum = present.reduce((a, p) => a + (p.checks_all_sum ?? 0), 0);
  return { count, avg: count > 0 ? sum / count : null };
}

/**
 * Пометка под суммой «к выплате» в предварительном месяце: командировочные и администраторская надбавка
 * добавляются панелью при выплате KPI (решение владельца 28–29.10.2026). null — пометки нет.
 */
export function netNoteKey(r: { away: boolean; role: string }, final: boolean): "away_net_note" | "away_admin_net_note" | "admin_net_note" | null {
  if (final) return null;
  const admin = r.role === "admin";
  if (r.away && admin) return "away_admin_net_note";
  if (r.away) return "away_net_note";
  if (admin) return "admin_net_note";
  return null;
}
